import { css } from '@emotion/react';
import { CepId, TRAVELER_TYPE, Traveler as TravelerType } from '@flysas-tech/book-value-stream-models';
import { ActionChip, Avatar, BREAKPOINTS, Divider, Heading, Paragraph, tokens } from '@flysas-tech/commercial-ui';
import { t } from 'i18n';
import { SavedTraveler } from 'shared';

import { getGenderLabel, getTierLevelForAvatar } from '../../domain/utils';
import { Card } from '../Card';

import { InfantSeatingNotice } from './InfantSeatingNotice';
import { SavedTravelers } from './SavedTravelers';

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${tokens.dimension['space-6']};
  `,
  headingContainer: css`
    display: flex;
    flex-direction: column;
  `,
  titleContainer: css`
    display: flex;
    gap: ${tokens.dimension['space-4']};
    align-items: center;
    justify-content: space-between;
  `,
  travelerContainer: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${tokens.dimension['space-4']};
    margin-top: ${tokens.dimension['space-4']};

    & p:first-of-type {
      font-weight: 700;
    }

    & button:last-of-type {
      margin-right: 0;
      margin-left: auto;
    }

    @container main-container (min-width: ${BREAKPOINTS.layout.md}px) {
      flex-wrap: nowrap;
    }
  `,
  traveler: css`
    display: flex;
    gap: ${tokens.dimension['space-4']};
  `,
  travelerInfo: css`
    display: inline;
    list-style: none;
    color: ${tokens.color['gray'][40]};
    font-size: ${tokens.fontSize.paragraph['paragraph-2']};
    padding: 0;
    li {
      display: inline-block;
      &:after {
        content: ',\0020';
        white-space: pre;
      }
      &:last-of-type:after {
        content: '';
      }
    }
  `,
  avatar: css`
    flex-shrink: 0;
  `,
};

type Props = {
  title: string;
  traveler: TravelerType;
  savedTravelers: SavedTraveler[];
  travelDate: string;
  onEdit: (id: string) => void;
  updateTraveler: (id: string, traveler: Partial<TravelerType>) => void;
  cepId?: CepId;
};

export const Traveler = ({ title, traveler, savedTravelers, onEdit, updateTraveler, travelDate }: Props) => {
  const { firstName, middleName, lastName, gender, dateOfBirth, email, phoneNumber, frequentTraveler, type } = traveler;

  const ebTier = getTierLevelForAvatar(frequentTraveler?.tier ?? '');

  const isInfant = type === TRAVELER_TYPE.INFANT;

  const fullName = `${firstName} ${middleName ?? ''} ${lastName}`;
  const travelerInfo = [
    t(getGenderLabel(gender)),
    dateOfBirth,
    email,
    phoneNumber,
    frequentTraveler?.frequentTravelerNumber,
  ].filter(Boolean);

  return (
    <Card css={styles.container}>
      <div css={styles.headingContainer}>
        <div css={styles.titleContainer}>
          <Heading as="h2" size="title-4">
            {title}
          </Heading>
          <SavedTravelers
            traveler={traveler}
            savedTravelers={savedTravelers}
            travelDate={travelDate}
            updateTraveler={updateTraveler}
          />
        </div>
        {isInfant && <InfantSeatingNotice />}
      </div>

      <Divider direction="horizontal" variant="subtle" />

      <div css={styles.travelerContainer}>
        <div css={styles.traveler}>
          <Avatar name={fullName} size="xs" css={styles.avatar} tier={ebTier} imgUrl={traveler.profileImage?.url} />
          <div>
            <Paragraph color="primary" size="paragraph-4">
              {fullName}
            </Paragraph>
            <ul css={styles.travelerInfo}>
              {travelerInfo.map((fact, index) => (
                <li key={String(index)}>{fact}</li>
              ))}
            </ul>
          </div>
        </div>
        <ActionChip
          data-testid="traveler-edit-button"
          border={false}
          icon="Edit"
          iconPlace="left"
          label={t('common:edit')}
          size="small"
          onClick={() => onEdit(traveler.id)}
        />
      </div>
    </Card>
  );
};
